<script setup>
import {ref, watch} from 'vue';
import BetCalcBetType from "@/components/bet_calc/BetCalcBetType.vue";
import BetCalcStake from "@/components/bet_calc/BetCalcStake.vue";
import BetCalcSelectionOdds from "@/components/bet_calc/BetCalcSelectionOdds.vue";
import BaseInputFloatingLabelNumber from "@/components/forms/BaseInputFloatingLabelNumber.vue";


// Define props
const props = defineProps({
  backData: Object, // Receive backData as a prop
  label: {
    type: String,
    default: 'Back Bet:',
  },
  oddsType: String
});

// Emit function for updating backData in the parent
const emit = defineEmits(['update:backData']);

// Create a local ref version of backBet
const backBet = ref({
  odds: props.backData?.odds || '2',
  stake: props.backData?.stake || '',
  commission: props.backData?.commission || 0,
  type: props.backData?.type || 'qualifying',
  lossValue: props.backData?.lossValue || 0,
  eachWayTerms: props.backData?.eachWayTerms || false
});


// Watch for changes in backBet and emit the updates to the parent
watch(backBet, (newBackBet) => {
  emit('update:backData', newBackBet);
}, {deep: true});
</script>


<template>
  <div class="bet-card">
    <div class="bet-card-head">
      <h6 class="heading-small">Back Bet:</h6>
    </div>
    <BetCalcBetType v-model="backBet.type"/>


    <div class="bet-card-body">
      <BetCalcStake v-model="backBet.stake" label="Stake"/>

      <!-- Conditional loss value input based on bet type -->
      <BaseInputFloatingLabelNumber
          v-if="backBet.type === 'rf'"
          :config="{ label: 'Loss Value (£)', name: 'back-loss-value', hasClearButton: false, classes: { error: 'has-error' } }"
          v-model="backBet.lossValue"/>

      <BetCalcSelectionOdds
          :oddsType="oddsType"
          label="Odds:"
          @update:decimalOdds="value => backBet.odds = value"
          :initial-decimal-odds="backBet.odds"
      />

      <!-- Commission input -->
      <div>
        <BaseInputFloatingLabelNumber
            :config="{ label: 'Commission (%)', name: 'back-commission', hasClearButton: false, classes: { error: 'has-error' } }"
            v-model="backBet.commission"/>
      </div>

      <BetCalcSelectionOdds
          v-if="backBet.eachWayTerms"
          oddsType="fractional"
          label="EW terms:"
          @update:decimalOdds="value => backBet.eachWayTerms = value"
          initial-denominator="5"
      />
    </div>
  </div>
</template>


<style scoped>
.bet-card {
  background-color: var(--clr-accent-40);
  color: var(--clr-black-100);
  padding-bottom: 1rem;
}

.bet-card-head {
  background-color: var(--clr-accent-40);
  display: flex;
  flex-direction: row;
  gap: 1rem;
  align-items: center; /* Ensure vertical alignment */
  justify-content: center; /* Center horizontally */
  padding: 1rem;
  border-bottom: 2px solid var(--clr-accent-30);
  height: 68px;
}

.heading-small {
  margin: 0; /* Remove default margin */
  text-align: center; /* Center text within heading */
  padding: 0;
  flex-shrink: 0;
}


.bet-card-body {
  padding-inline: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex-direction: column;
}


@media (min-width: 800px) {
  .bet-card-body {
    flex-direction: row;
    gap: 5%;
  }

  .bet-card-body > * {
    flex: 1 1 47.5%; /* Allow elements to grow and shrink, and set their base width to approximately 47.5% */
    box-sizing: border-box; /* Include padding and border in the element's total width and height */
    max-width: 47.5%;

  }
}

</style>
